import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Pencil, Trash2, GripVertical, Eye } from 'lucide-react';
import {
  PLANNING_STATUSES,
  PLANNED_FORMATS,
  PRIORITIES,
  type PlanningItem,
  type PlanningStatus,
  useChangePlanningStatus,
} from '@/hooks/useContentPlanning';
import { useState } from 'react';
import { toast } from 'sonner';

interface Props {
  items: PlanningItem[];
  onEdit: (item: PlanningItem) => void;
  onDelete: (item: PlanningItem) => void;
  onPreview?: (item: PlanningItem) => void;
}

export function ContentPlanningKanban({ items, onEdit, onDelete, onPreview }: Props) {
  const changeStatus = useChangePlanningStatus();
  const [dragOver, setDragOver] = useState<PlanningStatus | null>(null);

  const grouped: Record<PlanningStatus, PlanningItem[]> = {
    suggested: [],
    planning: [],
    in_development: [],
    in_review: [],
    published: [],
    archived: [],
  };
  for (const it of items) grouped[it.planning_status]?.push(it);

  const formatLabel = (v: string | null) =>
    PLANNED_FORMATS.find((f) => f.value === v)?.label ?? null;

  const priorityMeta = (v: string | null) =>
    PRIORITIES.find((p) => p.value === v);

  const handleDrop = async (status: PlanningStatus, e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(null);
    const id = e.dataTransfer.getData('text/plain');
    if (!id) return;
    const item = items.find((i) => i.id === id);
    if (!item || item.planning_status === status) return;
    try {
      await changeStatus.mutateAsync({ id, status });
      toast.success(`Movido a "${PLANNING_STATUSES.find((s) => s.value === status)?.label}"`);
    } catch (e: any) {
      toast.error(e?.message ?? 'No se pudo cambiar el estado');
    }
  };

  return (
    <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-5">
      {PLANNING_STATUSES.map((status) => {
        const list = grouped[status.value];
        const isOver = dragOver === status.value;
        return (
          <div
            key={status.value}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(status.value);
            }}
            onDragLeave={() => setDragOver((cur) => (cur === status.value ? null : cur))}
            onDrop={(e) => handleDrop(status.value, e)}
            className={`flex flex-col rounded-lg border bg-muted/30 ${isOver ? 'ring-2 ring-primary' : ''}`}
          >
            <div className="flex items-center justify-between px-3 py-2 border-b">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={status.color}>{status.label}</Badge>
                <span className="text-xs text-muted-foreground">{list.length}</span>
              </div>
            </div>

            <div className="flex flex-col gap-2 p-2 min-h-[200px] max-h-[70vh] overflow-y-auto">
              {list.map((item) => {
                const prio = priorityMeta(item.priority);
                return (
                  <Card
                    key={item.id}
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData('text/plain', item.id)}
                    className="cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow"
                  >
                    <CardHeader className="p-3 pb-2 flex flex-row items-start gap-2 space-y-0">
                      <GripVertical className="h-4 w-4 text-muted-foreground/50 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm line-clamp-2">{item.name}</div>
                        {item.category && (
                          <div className="text-[11px] text-muted-foreground mt-0.5">{item.category}</div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="p-3 pt-0 space-y-2">
                      {formatLabel(item.planned_format) && (
                        <Badge variant="outline" className="text-[10px] font-normal max-w-full">
                          <span className="truncate">{formatLabel(item.planned_format)}</span>
                        </Badge>
                      )}
                      <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                        {prio && (
                          <Badge variant="outline" className={`${prio.color} text-[10px]`}>{prio.label}</Badge>
                        )}
                        {item.responsible && (
                          <span className="text-muted-foreground truncate">👤 {item.responsible}</span>
                        )}
                      </div>
                      {item.prospect_route && (
                        <div className="text-[11px] text-muted-foreground truncate">📍 {item.prospect_route}</div>
                      )}
                      <div className="flex justify-end gap-1 pt-1">
                        {onPreview && (
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onPreview(item)} title="Previsualizar">
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(item)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onDelete(item)}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              {list.length === 0 && (
                <div className="text-xs text-muted-foreground/60 text-center py-6">
                  Arrastra contenidos aquí
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
